// apps/frontend/src/lib/pipeline-preview/graph-utils.ts
import type { PipelineNode, PipelineEdge } from '@/pages/pipelines/types/pipeline';
import type { ExecutionPath } from './types';

/**
 * Find parent nodes (input nodes) of a given node
 */
export function findParentNodes(
  nodes: PipelineNode[],
  edges: PipelineEdge[],
  nodeId: string
): PipelineNode[] {
  const parentIds = edges
    .filter(edge => edge.target === nodeId)
    .map(edge => edge.source);

  return nodes.filter(node => parentIds.includes(node.id));
}

/**
 * Find child nodes (output nodes) of a given node
 */
export function findChildNodes(
  nodes: PipelineNode[],
  edges: PipelineEdge[],
  nodeId: string
): PipelineNode[] {
  const childIds = edges
    .filter(edge => edge.source === nodeId)
    .map(edge => edge.target);

  return nodes.filter(node => childIds.includes(node.id));
}

/**
 * Build execution path from SOURCE to target node using BFS backward traversal
 * Returns path with nodes in execution order (SOURCE first, target last)
 */
export function buildExecutionPath(
  nodes: PipelineNode[],
  edges: PipelineEdge[],
  targetNodeId: string
): ExecutionPath {
  // Find the target node
  const targetNode = nodes.find(n => n.id === targetNodeId);
  if (!targetNode) {
    return {
      nodes: [],
      isValid: false,
      error: `Node with id "${targetNodeId}" not found`,
    };
  }

  // If target is already a SOURCE node, return it directly
  if (targetNode.data.type === 'SOURCE') {
    return {
      nodes: [targetNode],
      isValid: true,
    };
  }

  // BFS backward from target to SOURCE
  const visited = new Set<string>();
  const queue: string[] = [targetNodeId];
  const parentMap = new Map<string, string>();

  visited.add(targetNodeId);

  let sourceNodeId: string | null = null;

  while (queue.length > 0) {
    const currentId = queue.shift()!;
    const currentNode = nodes.find(n => n.id === currentId);

    if (currentNode?.data.type === 'SOURCE') {
      sourceNodeId = currentId;
      break;
    }

    const parents = findParentNodes(nodes, edges, currentId);
    for (const parent of parents) {
      if (!visited.has(parent.id)) {
        visited.add(parent.id);
        queue.push(parent.id);
        parentMap.set(parent.id, currentId);
      }
    }
  }

  // If no SOURCE found, return invalid path
  if (!sourceNodeId) {
    return {
      nodes: [],
      isValid: false,
      error: `No path from SOURCE to node "${targetNodeId}"`,
    };
  }

  // Reconstruct path from SOURCE to target
  const path: string[] = [];
  let currentId: string | undefined = sourceNodeId;

  while (currentId) {
    path.push(currentId);
    if (currentId === targetNodeId) {
      break;
    }
    currentId = parentMap.get(currentId);
  }

  // Convert node IDs to nodes
  const pathNodes = path
    .map(id => nodes.find(n => n.id === id))
    .filter((n): n is PipelineNode => n !== undefined);

  return {
    nodes: pathNodes,
    isValid: true,
  };
}
